/**
 * @file InstallPrompt.jsx
 * @description PWA install prompt component for encouraging app installation
 */

import React, { useState, useEffect } from 'react';
import { Icon } from "./Icon";

export const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if running in PWA mode
    if (window.navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      
      // Save the event so it can be triggered later
      setDeferredPrompt(e);
      
      // Check if user has previously dismissed the prompt
      const dismissed = localStorage.getItem('install-prompt-dismissed');
      const dismissedTime = localStorage.getItem('install-prompt-dismissed-time');
      
      // Show prompt if not dismissed or if dismissed more than 7 days ago
      if (!dismissed || (dismissedTime && Date.now() - parseInt(dismissedTime) > 7 * 24 * 60 * 60 * 1000)) {
        setShowInstallPrompt(true);
      }
    };

    // Listen for app installation
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
      
      // Remove dismissed flag since app is now installed
      localStorage.removeItem('install-prompt-dismissed');
      localStorage.removeItem('install-prompt-dismissed-time');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    
    // Remember that user dismissed the prompt
    localStorage.setItem('install-prompt-dismissed', 'true');
    localStorage.setItem('install-prompt-dismissed-time', Date.now().toString());
  };

  // Don't show if already installed or no prompt available
  if (isInstalled || !showInstallPrompt || !deferredPrompt) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: '16px',
      left: '16px',
      right: '16px',
      backgroundColor: 'var(--md-sys-color-surface-container-high)',
      border: '1px solid var(--md-sys-color-outline)',
      borderRadius: '16px',
      padding: '16px',
      boxShadow: 'var(--md-sys-elevation-level3)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      maxWidth: '400px',
      margin: '0 auto'
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <Icon
          name="installMobile"
          style={{ 
            fontSize: '24px', 
            color: 'var(--md-sys-color-primary)',
            flexShrink: 0,
            marginTop: '2px'
          }}
        />
        <div style={{ flex: 1 }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '500',
            color: 'var(--md-sys-color-on-surface)',
            margin: '0 0 4px 0'
          }}>
            Add to Home Screen
          </h3>
          <p style={{
            fontSize: '14px',
            color: 'var(--md-sys-color-on-surface-variant)',
            margin: '0',
            lineHeight: '1.4'
          }}>
            Install memorize.life for quick access to your Bible reading challenges and offline reading.
          </p>
        </div>
        <button
          onClick={handleDismiss}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            color: 'var(--md-sys-color-on-surface-variant)',
            flexShrink: 0
          }}
          aria-label="Dismiss install prompt"
        >
          <Icon name="close" style={{ fontSize: '20px' }} />
        </button>
      </div>
      
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          onClick={handleDismiss}
          style={{
            backgroundColor: 'transparent',
            color: 'var(--md-sys-color-primary)',
            border: 'none',
            borderRadius: '20px',
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer'
          }}
        >
          Maybe Later
        </button>
        <button
          onClick={handleInstallClick}
          style={{
            backgroundColor: 'var(--md-sys-color-primary)',
            color: 'var(--md-sys-color-on-primary)',
            border: 'none',
            borderRadius: '20px',
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Icon name="install" style={{ fontSize: '18px' }} />
          Install App
        </button>
      </div>
    </div>
  );
};
